/**
 * MODELO DE PÁGINA — implementação de referência do PADRÃO DESIGN.
 *
 * Copie este arquivo ao criar uma tela de listagem e troque os dados de exemplo pelos reais.
 * Ele existe para que página nova NASÇA no padrão: antes, o modelo era um esqueleto com um
 * placeholder no meio, e toda tela nova precisava ser convertida depois — foi essa a origem
 * da dívida de layout paga tela a tela em 03/09/2026.
 *
 * Os 11 itens do pacote, e onde cada um aparece aqui:
 *   1. cabeçalho .................. `c4u-page-header c4u-page-header--in-panel` (título DENTRO do painel) + título e subtítulo pelas classes
 *   2. botão + na barra ........... `AddButton` dentro de `c4u-filters__actions`, nunca no topo
 *   3. barra de filtros ........... `c4u-filters__row` > `__fields` + `__actions`
 *   4. limpar filtros ............. `ClearFiltersButton`, desabilitado quando não há o que limpar
 *   5. painel único ............... `c4u-panel` envolvendo filtros e lista
 *   6. caixa explicativa .......... `c4u-info-box`, ACIMA da lista (opcional)
 *   7. contagem ................... `ResultCount shown/total`
 *   8. tabela ..................... `c4u-table__header` + `c4u-table__row`
 *   9. poda de colunas ............ `c4u-col--xl|lg|md|sm`, na ordem de sacrifício
 *  10. lista em cartão no celular . vem de graça pelas classes — exige `data-label` em TODA célula
 *      (a linha não é clicável: ações só pelos botões — 04/09/2026)
 *  11. paginação ................. `StandardPagination`
 *
 * REGRAS QUE O ARQUIVO NÃO CONSEGUE IMPOR SOZINHO:
 * - Nunca escrever `style`/`sx` que repita o que a classe faz (`display`, `gridTemplateColumns`,
 *   largura de botão de ação, raio, fundo do painel). Inline vence o CSS e o responsivo morre
 *   em silêncio: o desktop parece certo e o celular continua em tabela.
 * - O `sx` que PODE ficar na célula é a largura da coluna (`flex`/`minWidth`) — o CSS do cartão
 *   já a neutraliza com `!important` no celular.
 * - `data-label` em toda célula: é ele que vira o rótulo do valor no modo cartão.
 * - `maxLength` em todo campo de texto.
 * - Página nova exige 4 lugares: PageType + validPages + protectedPages + switch no index.tsx.
 *
 * O PADRÃO MORA EM TRÊS LUGARES — mexer num obriga a mexer nos outros (regra do Hilton,
 * 03/09/2026). `src/global.css` é a fonte da verdade, porque é o comportamento real;
 * `admin.clinic4us/design-system.html` carrega a regra escrita e o porquê; este arquivo é o
 * exemplo executável. Quando os três discordam, o sistema ensina coisas diferentes — foi o que
 * aconteceu entre 02 e 03/09, com a régua escrita no documento e este arquivo ainda um esqueleto.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete, Edit, InfoOutlined, RestoreFromTrash } from "@mui/icons-material";
import { redirectToLogin } from "../services/httpClient";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, {
  ITENS_POR_PAGINA_PADRAO,
} from "../components/Pagination/StandardPagination";
import { useNavigation } from "../contexts/RouterContext";
import { colors } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";

const NAME_MAX = 100;

// Dentro do painel branco único as seções são só espaçamento: fundo e borda próprios
// virariam caixa dentro de caixa.
const sectionStyle = { mb: 3 };

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

interface MenuItemProps {
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
}

interface UserSession {
  email: string;
  alias: string;
  clinicName: string;
  role: string;
  permissions: string[];
  menuItems: MenuItemProps[];
  loginTime: string;
}

/** Troque por sua entidade real. */
interface SampleItem {
  id: string;
  name: string;
  description: string;
  category: string;
  owner: string;
  updatedAt: string;
  active: boolean;
}

const SAMPLE_ITEMS: SampleItem[] = [
  { id: "1", name: "Primeiro registro", description: "Detalhe secundário do registro", category: "Categoria A", owner: "Maria Souza", updatedAt: "01/09/2026", active: true },
  { id: "2", name: "Segundo registro", description: "", category: "Categoria B", owner: "João Lima", updatedAt: "28/08/2026", active: true },
  { id: "3", name: "Terceiro registro", description: "Outro detalhe", category: "Categoria A", owner: "Ana Prado", updatedAt: "15/08/2026", active: false },
];

const PageModel: React.FC = () => {
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const { goToDashboard } = useNavigation();

  const [items] = useState<SampleItem[]>(SAMPLE_ITEMS);
  const [loading] = useState(false);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState<"ativos" | "inativos" | "todos">("ativos");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);

  useEffect(() => {
    const simulatedUserSession: UserSession = {
      email: "user@clinic4us.com",
      alias: "User Demo",
      clinicName: "Clínica Demo",
      role: "Professional",
      permissions: ["view_schedule", "manage_appointments"],
      menuItems: [
        { label: "Dashboard", href: "/dashboard" },
        { label: "Agenda", href: "/schedule" },
        { label: "Pacientes", href: "/patients" },
        { label: "Relatórios", href: "/reports" },
      ],
      loginTime: new Date().toISOString(),
    };
    setUserSession(simulatedUserSession);
  }, []);

  // A busca ignora acento e caixa: sem isto, "jose" não encontra "José" e o usuário
  // conclui que o registro não existe.
  const normalize = (value: string) =>
    value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

  const categories = useMemo(
    () => Array.from(new Set(items.map((i) => i.category))).sort(),
    [items],
  );

  const filteredItems = useMemo(() => {
    const term = normalize(search.trim());
    return items.filter((item) => {
      if (status === "ativos" && !item.active) return false;
      if (status === "inativos" && item.active) return false;
      if (category && item.category !== category) return false;
      if (term && !normalize(item.name).includes(term)) return false;
      return true;
    });
  }, [items, search, category, status]);

  const hasFilterChanges = search !== "" || category !== "" || status !== "ativos";

  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setStatus("ativos");
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const pagedItems = useMemo(
    () => filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filteredItems, currentPage, itemsPerPage],
  );

  // O filtro pode deixar o usuário numa página que não existe mais.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const handleRevalidateLogin = () => {
    localStorage.removeItem("clinic4us-user-session");
    localStorage.removeItem("clinic4us-remember-me");
    redirectToLogin();
  };

  const openCreate = () => {};
  const openEdit = (_item: SampleItem) => {};
  const toggleActive = (_item: SampleItem) => {};

  if (!userSession) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={userSession.email}
        userProfile={userSession.role}
        clinicName={userSession.clinicName}
        notificationCount={0}
        onRevalidateLogin={handleRevalidateLogin}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          {/* 1. Cabeçalho: o topo APRESENTA a tela; a regra detalhada vai na caixa
              explicativa e o resto no FAQ. Subtítulo de uma linha. */}
          <Box className="c4u-page-header c4u-page-header--in-panel">
            {/* Página-FILHA (chegou de outra lista) leva o Voltar aqui, à esquerda do título:
                <IconButton className="c4u-page-header__back" size="small" title="Voltar para Usuários"
                  onClick={() => navigateTo("entity-users", { entityId, entityName })}>
                  <ArrowBack />
                </IconButton>
                Página de menu não tem: o menu é o caminho. */}
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Título da página
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Uma linha dizendo o que se faz aqui.
              </Typography>
            </Box>
            <FaqButton />
          </Box>

          {/* HIERARQUIA (Hilton, 19/09/2026): título apresenta → ESCOPO diz de quem é o dado →
              filtros recortam dentro dele → contagem → lista. Página interna de Usuários abre o
              painel com o escopo, ANTES da barra de filtros — nunca no título, nunca abaixo do filtro:
                <div className="c4u-scope-line"><strong>{professionalName}</strong> · {especialidade}</div> */}
          {/* 5. Painel único: filtros e lista moram no MESMO cartão branco. */}
          <div className="c4u-panel">
            {/* 3. Barra de filtros */}
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por nome"
                  placeholder="Buscar registro"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  fullWidth
                  sx={searchFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: NAME_MAX }}
                />
                <TextField
                  select
                  size="small"
                  label="Categoria"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  {/* Opção vazia: em FILTRO é "Todos"/"Todas"; em campo de FORMULÁRIO é
                      "Selecione" — texto simples, sem itálico nem reticências. */}
                  <MenuItem value="">Todas</MenuItem>
                  {categories.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as "ativos" | "inativos" | "todos");
                    setCurrentPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="ativos">Ativos</MenuItem>
                  <MenuItem value="inativos">Inativos</MenuItem>
                  <MenuItem value="todos">Todos</MenuItem>
                </TextField>
              </div>

              {/* 2 e 4: incluir e limpar ficam JUNTOS, no fim da barra. */}
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <AddButton onClick={openCreate} title="Incluir registro" />
              </div>
            </div>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loading && (
              <>
                {/* 6. Caixa explicativa (opcional): a regra que o usuário precisa saber ANTES
                    de agir. Fica acima da lista — no fim, ninguém lê. Use
                    `c4u-info-box--warning` quando for alerta. */}
                <Box className="c4u-info-box">
                  <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                  <Typography>
                    A regra que o usuário precisa conhecer antes de mexer aqui. Duas linhas no
                    máximo — o detalhamento vai para o FAQ.
                  </Typography>
                </Box>

                <Box sx={sectionStyle}>
                  {/* 7. Contagem */}
                  <ResultCount shown={filteredItems.length} total={items.length} />

                  {filteredItems.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      {items.length === 0
                        ? "Nenhum registro cadastrado — inclua o primeiro no botão acima."
                        : "Nenhum registro encontrado com os filtros aplicados."}
                    </Typography>
                  ) : (
                    <Box className="admin-plans-table">
                      {/* 8. Cabeçalho da tabela. 9. As classes c4u-col--* definem a ORDEM DE
                          SACRIFÍCIO: xl some primeiro (<1200px), depois lg (<992), md (<768)
                          e sm (<576). Colunas sem classe nunca somem — reserve isso para o
                          que identifica o registro, o status e as ações. */}
                      <Box className="c4u-table__header">
                        <Box className="admin-plans-header-cell" sx={{ flex: "2 1 280px", minWidth: 240 }}>
                          Nome
                        </Box>
                        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 200px", minWidth: 160 }}>
                          Categoria
                        </Box>
                        <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "1 1 200px", minWidth: 160 }}>
                          Responsável
                        </Box>
                        <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 130px" }}>
                          Atualizado em
                        </Box>
                        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px" }}>
                          Status
                        </Box>
                        <Box className="admin-plans-header-cell no-print" sx={{ flex: "0 0 120px", justifyContent: "flex-end" }}>
                          Ações
                        </Box>
                      </Box>

                      <Box className="admin-plans-table-body">
                        {pagedItems.map((item) => (
                          // A linha NÃO abre nada ao clicar (decisão do Hilton, 04/09/2026): com a
                          // coluna de ações, o clique na linha só criava o risco de abrir a edição por
                          // cima de um botão que esquecesse o stopPropagation. Faria sentido apenas
                          // numa lista sem botões.
                          <Box
                            key={item.id}
                            className="c4u-table__row"
                          >
                            {/* 10. `data-label` em TODA célula: no celular a linha vira cartão
                                e é ele que rotula cada valor. Sem isso o cartão sai com números
                                soltos sem dizer do que são. */}
                            <Box
                              className="admin-plans-cell admin-plans-name"
                              sx={{ flex: "2 1 280px", minWidth: 240, gap: 0.5, flexWrap: "wrap" }}
                              data-label="Nome"
                            >
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{item.name}</Typography>
                              {item.description && (
                                <Typography sx={{ fontSize: 11, color: "#adb5bd", width: "100%" }}>
                                  {item.description}
                                </Typography>
                              )}
                            </Box>
                            <Box
                              className="admin-plans-cell c4u-col--lg"
                              sx={{ flex: "1 1 200px", minWidth: 160 }}
                              data-label="Categoria"
                            >
                              <Typography sx={{ fontSize: 13 }}>{item.category}</Typography>
                            </Box>
                            <Box
                              className="admin-plans-cell c4u-col--xl"
                              sx={{ flex: "1 1 200px", minWidth: 160 }}
                              data-label="Responsável"
                            >
                              <Typography sx={{ fontSize: 13 }}>{item.owner}</Typography>
                            </Box>
                            <Box
                              className="admin-plans-cell c4u-col--md"
                              sx={{ flex: "0 0 130px" }}
                              data-label="Atualizado em"
                            >
                              <Typography sx={{ fontSize: 13 }}>{item.updatedAt}</Typography>
                            </Box>
                            <Box
                              className="admin-plans-cell"
                              sx={{ flex: "0 0 110px" }}
                              data-label="Status"
                            >
                              <Chip
                                label={item.active ? "Ativo" : "Inativo"}
                                size="small"
                                sx={{
                                  color: "white",
                                  fontWeight: 600,
                                  backgroundColor: item.active ? colors.success : "#adb5bd",
                                }}
                              />
                            </Box>
                            <Box
                              className="admin-plans-cell admin-plans-actions no-print"
                              sx={{ flex: "0 0 120px" }}
                              data-label="Ações"
                            >
                              {/* `c4u-row-action` dá o botão redondo do sistema: 32px no
                                  desktop e 44px no celular, alvo de toque do polegar. */}
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                                <Tooltip title="Editar registro">
                                  <IconButton
                                    className="c4u-row-action"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEdit(item);
                                    }}
                                    sx={rowActionSx(colors.primary, colors.primaryHover)}
                                  >
                                    <Edit sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={item.active ? "Inativar registro" : "Reativar registro"}>
                                  <IconButton
                                    className="c4u-row-action"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleActive(item);
                                    }}
                                    sx={rowActionSx(
                                      item.active ? "#dc3545" : colors.success,
                                      item.active ? "#c82333" : "#218838",
                                    )}
                                  >
                                    {item.active ? (
                                      <Delete sx={{ fontSize: "1rem" }} />
                                    ) : (
                                      <RestoreFromTrash sx={{ fontSize: "1rem" }} />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* 11. Paginação */}
                  {filteredItems.length > 0 && (
                    <StandardPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredItems.length}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(n) => {
                        setItemsPerPage(n);
                        setCurrentPage(1);
                      }}
                    />
                  )}
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default PageModel;
